import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { ResizablePanel } from 'Src/components/ResizablePanel/ResizablePanel';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import {
  getAdminSUAllOutboxInvites,
  getAdminSUOutboxInvites,
  getAdminSUOutboxInvitesCount
} from 'Src/helpers/service/adminSU/invitesTable';
import { INVITE_TABS } from 'Src/consts/invite';
import { InviteOutboxSummary } from 'Src/components/InviteSummary/InviteOutboxSummary';
import { getCreatedAtWithTime, getInviteOutboxStatus } from 'Src/helpers/accessor/accessor';
import { InviteDetailsDrawer } from 'Src/views/GenericView/AdminView/Invites/InviteDetailsDrawer';
import 'Src/views/GenericView/AdminView/Invites/SchoolInvitesHub.scss';
import './UnionTournamentInvites.scss';
import { UnionInvitesSegments } from '../Tournaments/UnionInvitesSegments';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Tournament',
    field: 'tournament.name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['tournament', 'name']
  },
  {
    text: 'School',
    field: 'invitedSchool.name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['invitedSchool', 'name']
  },
  {
    text: 'Sent',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAtWithTime
  }
];

interface State {
  items: any[];
  currentPage: number;
  selectedItems: any[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  userTags: any[];
  total: number;
  basePath: string;

  currentTabIndex: number;
  viewedInvite: any | null;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class AdminSUInvitesOutbox extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      userTags: [],
      total: 0,
      basePath: '',

      currentTabIndex: 0,
      viewedInvite: null
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getAdminSUOutboxInvites(user, serverQueryFilter);
    const getItemsCountPromise = getAdminSUOutboxInvitesCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    this.setState({ viewedInvite: this.state.items[index], currentTabIndex: 0 });
  };

  onItemSelect = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAdminSUAllOutboxInvites(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernRange) {
      nextFilters = { ...nextFilters, [filterField]: options.modernRange };
    } else if (options && options.modernMultiValue) {
      nextFilters = { ...nextFilters, [filterField]: Array.isArray(filterValue) ? filterValue : [] };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      filters: {},
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onClearIndividualFilter = (filterField: string): void => {
    const nextFilters = { ...this.state.filters };
    delete nextFilters[filterField];
    const search: string[] = [];
    if (isSortExist(this.state.sortDirection, this.state.sortColumnsName)) {
      search.push(getSearchOrder(this.state.sortDirection, this.state.sortColumnsName));
    }
    if (isFilterExist2(nextFilters)) search.push(getSearchFilter(nextFilters));
    this.setState({ filters: nextFilters, selectedItems: [], isSelectAllChecked: false });
    this.props.history.push({ pathname: this.state.basePath, search: search.join('&') });
  };

  onCancelFilterClick = (): void => {
    this.setState({
      isShowFilter: false,
      filters: getFilters2(COLUMNS, parse(this.props.history.location.search))
    });
  };

  renderInviteTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total
    } = this.state;

    const actionItems = [];

    const filterOptions = {};

    return (
      <Grid2
        user={this.props.user}
        gridTitle="Tournament invite outbox"
        gridTitleContent={<UnionInvitesSegments active="outbox" />}
        toolbarSearchField="tournament.name"
        toolbarSearchPlaceholder="Search tournaments..."
        onToolbarSearchClear={() => this.onClearIndividualFilter('tournament.name')}
        isActionsInline={true}
        isCSV={false}
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        emptyIcon={faPaperPlane}
        emptyText="No sent tournament invites"
        emptyHint="Tournament invitations sent by this union will appear here."
        emptyFilteredText="No sent invites match your filters"
        emptyFilteredHint="Try changing or clearing the search and filters to see more invitations."
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        columnVisibilityKey="union-tournament-invites-outbox"
        fixedColumnFields={['tournament.name']}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onItemSelect={this.onItemSelect}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onClearIndividualFilter={this.onClearIndividualFilter}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
      />
    );
  }

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  renderSummary(): React.ReactNode {
    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a invite above</div>;
    }

    const { currentTabIndex } = this.state;
    const summaryTabs = this.getSummaryTabs();

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={INVITE_TABS} currentTabIndex={currentTabIndex}>
        {summaryTabs}
      </GenericSummary>
    );
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems } = this.state;

    const invite = selectedItems[0];

    switch (currentTabIndex) {
      case 0:
        return <InviteOutboxSummary invite={invite} />;
    }
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="schoolInvitesList unionTournamentInvitesList">
        <div className="row">
          <div className="col-md-12">
            {this.renderInviteTable()}
            {this.state.viewedInvite && (
              <InviteDetailsDrawer
                eventName={this.state.viewedInvite.tournament?.name || 'Tournament invite'}
                date={getCreatedAtWithTime(this.state.viewedInvite)}
                schoolName={this.state.viewedInvite.invitedSchool?.name || '-'}
                venue={
                  this.state.viewedInvite.tournament?.venue?.name ||
                  this.state.viewedInvite.tournament?.venue?.placeData?.name ||
                  '-'
                }
                status={getInviteOutboxStatus(this.state.viewedInvite)}
                folder="Tournament invite outbox"
                onClose={() => this.setState({ viewedInvite: null })}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}
